/**
 * IPI na base do ICMS (LC 87/96, art. 13 §2º; regra IT-ICMS-06), pelo indicador de consumidor final da nota:
 *
 *   indFinal = 0 (normal)          → o IPI NÃO compõe a base do ICMS
 *   indFinal = 1 (consumidor final) → o IPI compõe a base do ICMS
 *
 * O §2º só tira o IPI da base na operação entre contribuintes com produto para industrialização ou comercialização — é o
 * que indFinal 0 declara; consumidor final (indFinal 1, contribuinte ou não) leva o IPI na base. É também o critério do
 * Protheus (F4_INCIDE = F: IPI na base só para consumidor final).
 *
 * Usado pelo Confronto (regras.ts, IT-ICMS-06) e pelo Diagnóstico Protheus (cenarios/montar.ts → refino no portal).
 */
import type { ItemLido } from './tipos';

export type TratamentoIpi = 'INCLUI' | 'EXCLUI';

/** Diferença aceita entre a base informada e a calculada (arredondamento por item). */
export const TOLERANCIA_BASE_IPI = 0.05;

/** O que o indFinal da nota manda fazer com o IPI na base do ICMS; null quando a nota não traz indFinal. */
export function ipiPeloIndFinal(indFinal: string | null): TratamentoIpi | null {
  if (indFinal === '1') return 'INCLUI';
  if (indFinal === '0') return 'EXCLUI';
  return null;
}

/**
 * Base do ICMS do item sem e com o IPI (valor da operação: vProd − vDesc + vFrete + vSeg + vOutro + vII; campo aditivo
 * ausente vale zero). null quando o item não tem IPI ou vProd.
 */
export function basesIcmsComESemIpi(item: ItemLido): { semIpi: number; comIpi: number; vIPI: number } | null {
  const vIPI = item.ipi?.vTributo;
  if (!vIPI || vIPI <= 0 || item.vProd === null) return null;
  const semIpi = item.vProd - (item.vDesc ?? 0) + (item.vFrete ?? 0) + (item.vSeg ?? 0) + (item.vOutro ?? 0) + (item.vII ?? 0);
  return { semIpi, comIpi: semIpi + vIPI, vIPI };
}

/**
 * O que a base do ICMS informada no item mostra: INCLUI (bate com a base com IPI), EXCLUI (bate com a base sem IPI) ou
 * null quando não bate com nenhuma (base reduzida, rateio de despesas) ou o IPI é pequeno demais para distinguir.
 */
export function ipiPraticadoNaBase(item: ItemLido): TratamentoIpi | null {
  const bases = basesIcmsComESemIpi(item);
  const vBC = item.icms?.vBC;
  if (!bases || vBC === null || vBC === undefined || bases.vIPI <= 2 * TOLERANCIA_BASE_IPI) return null;
  if (Math.abs(vBC - bases.comIpi) <= TOLERANCIA_BASE_IPI) return 'INCLUI';
  if (Math.abs(vBC - bases.semIpi) <= TOLERANCIA_BASE_IPI) return 'EXCLUI';
  return null;
}
